/**
 * Timezone-aware date/time helpers for the scheduling system.
 *
 * The model: a class stores an absolute instant (`startAt`, a UTC Timestamp)
 * plus the IANA-labelled `timezone` it was scheduled in (e.g.
 * "Asia/Kolkata (IST)"). The instant is unambiguous; the label is kept so we can
 * show "the time everyone agreed on" alongside each viewer's own local time.
 *
 * These helpers are framework-agnostic and side-effect free.
 */
import { TIMEZONES } from 'features/admin/constants';
import { toDate } from 'utils';

/**
 * Make a stored meeting link safe to put in an `href`.
 *
 * Admins paste links by hand, and a link pasted without a scheme
 * ("teams.microsoft.com/l/meetup-join/…") is a *relative* URL: the Join button
 * would silently navigate inside the SPA instead of opening the meeting, which
 * reads to the user as "the button doesn't work". Bare hosts are promoted to
 * https; `javascript:`/`data:` are dropped entirely; custom app schemes
 * (`msteams:`, `zoommtg:`) are legitimate deep links and pass through.
 */
export function normalizeJoinUrl(raw) {
  const url = String(raw ?? '').trim();
  if (!url) return '';
  if (/^(javascript|data|vbscript):/i.test(url)) return '';
  if (url.startsWith('//')) return `https:${url}`;
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return url;
  return `https://${url}`;
}

/** Resolve the best join URL for a class: its own link, else the mentor's. */
export function joinLinkFor(classItem, mentor) {
  return normalizeJoinUrl(classItem?.meetingLink || mentor?.teamsLink || '');
}

/** How many minutes before the scheduled start the Join action unlocks. */
export const JOIN_OPENS_BEFORE_MIN = 15;

/**
 * Grace period after the scheduled end during which Join still works. Sessions
 * routinely overrun, and a button that dies on the exact minute strands whoever
 * clicked it a moment too late. (The `/join/:classId` email route already
 * allows 30 minutes; this is the in-app equivalent.)
 */
export const JOIN_GRACE_AFTER_MIN = 15;

/** Absolute end instant (ms) of a class — start + duration. Null when unknown. */
export function classEndMs(classItem) {
  const start = toDate(classItem?.startAt)?.getTime();
  if (start == null) return null;
  return start + (Number(classItem?.durationMin) || 60) * 60 * 1000;
}

/**
 * Statuses that still allow joining, given the time window is open.
 *
 * Crucially this is NOT just `scheduled`. The attendance pass stamps
 * `student_no_show` and `mentor_no_show` roughly 20 minutes after the start —
 * while the session is very much still running. A `scheduled`-only check
 * therefore made the Join button disappear mid-class for exactly the person who
 * was running late, which is the one moment they need it: the backend
 * explicitly supports a late join (the `/join` route stamps for 90 minutes and
 * reverts `student_no_show` back to `scheduled`), so the UI has to let them
 * click. Only `cancelled` and `completed` really close the door.
 *
 * `not_conducted` stays in the set even though it is now written only by a
 * submitted log — after the class, so the time window has closed anyway. It
 * costs nothing and keeps a status from silently falling out of the gate.
 */
const JOINABLE_STATUSES = new Set([
  'scheduled',
  'student_no_show',
  'mentor_no_show',
  'both_no_show',
  'not_conducted',
]);

/** A class is "live now" within a window around its scheduled start. */
export function isLiveNow(startMs, durationMin = 60, now = Date.now()) {
  if (startMs == null) return false;
  const opensAt = startMs - JOIN_OPENS_BEFORE_MIN * 60 * 1000;
  const endsAt = startMs + durationMin * 60 * 1000;
  return now >= opensAt && now <= endsAt;
}

/**
 * Is this class in its live window — from 15 minutes before the start to its
 * end? The predicate the Join gate and the dashboard buckets share, so they
 * can never disagree about which class is the one happening now. It opens
 * early on purpose (the class about to start is the one to spotlight), which
 * is why no label reads it directly: see `isClassInSession`.
 */
export function isClassLive(classItem, now = Date.now()) {
  if (!classItem || !JOINABLE_STATUSES.has(classItem.status)) return false;
  const start = toDate(classItem.startAt)?.getTime();
  return isLiveNow(start, classItem.durationMin, now);
}

/**
 * Has the session actually begun? Every "Live" label goes through this, and
 * every "Starting soon" through `isClassStartingSoon`, so a class nine minutes
 * from its start is never called live — `isClassLive` used to drive the label
 * too, and a ticket read "Live now · 69m left" for a session that hadn't begun.
 */
export function isClassInSession(classItem, now = Date.now()) {
  if (!isClassLive(classItem, now)) return false;
  return now >= toDate(classItem.startAt).getTime();
}

/** Inside the live window but before the start: the class is "Starting soon". */
export function isClassStartingSoon(classItem, now = Date.now()) {
  return isClassLive(classItem, now) && !isClassInSession(classItem, now);
}

/**
 * Is this class over? Judged on the session's END, never its start — a class
 * that has begun is still happening, and treating it as past is what makes a
 * live session (and its Join button) vanish from a dashboard.
 */
export function isClassOver(classItem, now = Date.now()) {
  if (!classItem) return false;
  if (classItem.status === 'completed') return true;
  // `mentor_no_show` / `student_no_show` are stamped ~20 min into the session,
  // so they do NOT by themselves mean the class is finished — let the clock say.
  const end = classEndMs(classItem);
  return end != null && end <= now;
}

/**
 * Join gating for both mentors and students: a class is joinable from 15
 * minutes before its scheduled start until shortly after it ends, and only
 * while it is still in the "scheduled" state.
 */
export function canJoinNow(classItem, now = Date.now()) {
  if (!classItem || !JOINABLE_STATUSES.has(classItem.status)) return false;
  const start = toDate(classItem.startAt)?.getTime();
  if (start == null) return false;
  const opensAt = start - JOIN_OPENS_BEFORE_MIN * 60 * 1000;
  const closesAt = (classEndMs(classItem) ?? start) + JOIN_GRACE_AFTER_MIN * 60 * 1000;
  return now >= opensAt && now <= closesAt;
}

/** Compact countdown for the locked Join button: "45s", "12m", "3h 05m". */
export function formatCountdown(ms) {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total}s`;
  const mins = Math.round(total / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${String(mins % 60).padStart(2, '0')}m`;
  const days = Math.round(hours / 24);
  return `${days}d`;
}

/** The possible states of the Join action. */
export const JOIN_STATE = {
  OPEN: 'open',         // joinable right now
  LOCKED: 'locked',     // scheduled, link ready, window not open yet
  NO_LINK: 'no_link',   // scheduled, but nobody has set a meeting link
  ENDED: 'ended',       // finished (or ran past the grace period)
  CANCELLED: 'cancelled',
};

/**
 * The single source of truth for the Join affordance — every card, row, hero
 * and modal renders from this, so all of them agree on whether joining is
 * possible and, crucially, *say why* when it isn't.
 *
 * Returns `{ state, url, live, opensInMs, label, hint }`.
 */
export function joinState(classItem, mentor, now = Date.now()) {
  const url = joinLinkFor(classItem, mentor);
  const base = { url, live: false, opensInMs: 0 };

  if (!classItem) return { ...base, state: JOIN_STATE.ENDED, label: 'Join', hint: '' };

  if (classItem.status === 'cancelled') {
    return { ...base, state: JOIN_STATE.CANCELLED, label: 'Cancelled', hint: 'This class was cancelled.' };
  }

  const start = toDate(classItem.startAt)?.getTime();
  const closesAt = start != null ? (classEndMs(classItem) ?? start) + JOIN_GRACE_AFTER_MIN * 60 * 1000 : null;
  const over = !JOINABLE_STATUSES.has(classItem.status) || (closesAt != null && now > closesAt);
  if (over) {
    return { ...base, state: JOIN_STATE.ENDED, label: 'Session ended', hint: 'This session is over.' };
  }

  if (!url) {
    return {
      ...base,
      state: JOIN_STATE.NO_LINK,
      label: 'No meeting link',
      hint: 'No meeting link has been set for this class yet — contact the LearnX team.',
    };
  }

  const opensAt = start != null ? start - JOIN_OPENS_BEFORE_MIN * 60 * 1000 : null;
  if (opensAt != null && now < opensAt) {
    const opensInMs = opensAt - now;
    return {
      ...base,
      state: JOIN_STATE.LOCKED,
      opensInMs,
      label: `Opens in ${formatCountdown(opensInMs)}`,
      hint: `Join unlocks ${JOIN_OPENS_BEFORE_MIN} minutes before the class starts.`,
    };
  }

  return {
    ...base,
    state: JOIN_STATE.OPEN,
    live: isLiveNow(start, classItem.durationMin, now),
    label: 'Join',
    hint: 'Opens your meeting in a new tab.',
  };
}

/** Human-readable file size, e.g. "1.4 MB". */
export function formatBytes(bytes = 0) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** i;
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

/** The viewer's own IANA zone, e.g. "Europe/London". */
export function localZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/**
 * Extract the IANA id from a timezone label.
 *   "Asia/Kolkata (IST)" -> "Asia/Kolkata"
 *   "UTC"                -> "UTC"
 * Falls back to the viewer's local zone when no label is given.
 */
export function ianaOf(tzLabel) {
  if (!tzLabel) return localZone();
  return tzLabel.split(' ')[0];
}

/** The short abbreviation embedded in a label: "Asia/Kolkata (IST)" -> "IST". */
export function abbrevOf(tzLabel) {
  if (!tzLabel) return '';
  const m = tzLabel.match(/\(([^)]+)\)/);
  return m ? m[1] : tzLabel;
}

/** Best-guess timezone label matching the viewer's zone, else "UTC". */
export function guessTimezoneLabel() {
  const local = localZone();
  return TIMEZONES.find((t) => ianaOf(t) === local) || 'UTC';
}

/** Offset (ms) of `iana` from UTC at the given instant. */
function tzOffsetMs(iana, date) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const p = {};
  for (const part of dtf.formatToParts(date)) p[part.type] = part.value;
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return asUTC - date.getTime();
}

/**
 * Convert a wall-clock time *in a given timezone* to a UTC millisecond instant.
 * This is the crux of correct scheduling: entering "3:30 PM IST" stores the same
 * instant regardless of the admin's own browser timezone. Refines once to stay
 * correct across DST boundaries (date-fns-tz's two-pass technique).
 *
 * @param {string} tzLabel  e.g. "Asia/Kolkata (IST)"
 * @param {string} dateStr  "YYYY-MM-DD"
 * @param {string} timeStr  "HH:mm"
 * @returns {number|null} epoch ms, or null if inputs are incomplete/invalid.
 */
export function zonedInputToUtc(tzLabel, dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = timeStr.split(':').map(Number);
  if ([y, mo, d, h, mi].some((n) => Number.isNaN(n))) return null;

  const iana = ianaOf(tzLabel);
  const guess = Date.UTC(y, mo - 1, d, h, mi, 0);
  const off1 = tzOffsetMs(iana, new Date(guess));
  let utc = guess - off1;
  const off2 = tzOffsetMs(iana, new Date(utc));
  if (off2 !== off1) utc = guess - off2;
  return utc;
}

/** Weekday labels, index 0 = Sunday … 6 = Saturday (matches Date.getDay()). */
export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WEEKDAY_LABELS_FULL = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

/**
 * Generate the absolute UTC instants for a weekly recurring series.
 *
 * The schedule repeats every week on the chosen weekdays at a per-weekday local
 * time (e.g. Mon 20:00, Wed 21:00) interpreted in `tzLabel`, starting on/after
 * `startDate`, until `count` sessions have been produced. Each instant is
 * resolved through `zonedInputToUtc`, so DST shifts are handled per-occurrence —
 * "8 PM EST" stays 8 PM wall-clock for the student even across a DST boundary.
 *
 * Iterating calendar dates in UTC avoids the host timezone skewing which weekday
 * a date falls on. Returns chronologically-ordered Dates (length ≤ count).
 *
 * @param {object} spec
 * @param {string} spec.startDate  "YYYY-MM-DD" — first eligible day
 * @param {string} spec.tzLabel    e.g. "America/New_York (EST)"
 * @param {Object<number,string>} spec.weekdayTimes  weekday (0–6) → "HH:mm"
 * @param {number} spec.count      total sessions to generate
 * @returns {Date[]}
 */
export function generateWeeklyOccurrences({ startDate, tzLabel, weekdayTimes = {}, count }) {
  const activeDays = Object.keys(weekdayTimes)
    .map(Number)
    .filter((d) => weekdayTimes[d]);
  if (!startDate || activeDays.length === 0 || !count || count < 1) return [];

  const [y, mo, d] = startDate.split('-').map(Number);
  if ([y, mo, d].some((n) => Number.isNaN(n))) return [];

  const out = [];
  const cursor = new Date(Date.UTC(y, mo - 1, d));
  const maxIterations = count * 7 + 366; // safety bound
  let guard = 0;

  while (out.length < count && guard < maxIterations) {
    const time = weekdayTimes[cursor.getUTCDay()];
    if (time) {
      const ds = `${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, '0')}-${String(cursor.getUTCDate()).padStart(2, '0')}`;
      const ms = zonedInputToUtc(tzLabel, ds, time);
      if (ms != null) out.push(new Date(ms));
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    guard += 1;
  }
  return out;
}

/** Date/time parts of an instant rendered in a specific timezone. */
function zonedParts(value, iana) {
  const d = toDate(value);
  if (!d) return null;
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const p = {};
  for (const part of dtf.formatToParts(d)) p[part.type] = part.value;
  return p;
}

/** "YYYY-MM-DD" for a `<input type="date">`, rendered in the class timezone. */
export function toZonedDateInput(value, tzLabel) {
  const p = zonedParts(value, ianaOf(tzLabel));
  return p ? `${p.year}-${p.month}-${p.day}` : '';
}

/** "HH:mm" for a `<input type="time">`, rendered in the class timezone. */
export function toZonedTimeInput(value, tzLabel) {
  const p = zonedParts(value, ianaOf(tzLabel));
  return p ? `${p.hour}:${p.minute}` : '';
}

/** Format an instant in a specific timezone with Intl options. */
export function formatInTimeZone(value, tzLabel, options) {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat(undefined, { ...options, timeZone: ianaOf(tzLabel) }).format(d);
}

/** True when a class's scheduled timezone differs from the viewer's own. */
export function differsFromLocal(tzLabel) {
  return Boolean(tzLabel) && ianaOf(tzLabel) !== localZone();
}

// ---- Recurring availability + one-time slot scheduling ----------------------
//
// These power the mentor Availability / Open Slot system and the admin reschedule
// dashboard. Recurring availability is stored as wall-clock minutes-from-midnight
// in a labelled timezone; resolving it onto a concrete calendar date goes through
// `zonedInputToUtc`, so every window is DST-correct for any future date.

/** "HH:mm" → minutes from midnight, e.g. "06:30" → 390. Null when unparseable. */
export function hhmmToMinutes(str) {
  if (!str) return null;
  const [h, m] = String(str).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/** Minutes from midnight → "HH:mm" (for <input type="time">), e.g. 390 → "06:30". */
export function minutesToHHMM(min) {
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** Friendly time from minutes-from-midnight, e.g. 390 → "6:30 AM". */
export function formatMinutes(min) {
  if (min == null || Number.isNaN(min)) return '—';
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m % 60).padStart(2, '0')} ${period}`;
}

/** Half-open overlap test: do [aStart,aEnd) and [bStart,bEnd) intersect? */
export function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

// ---- Overnight (cross-midnight) windows -------------------------------------
//
// A window whose end time is at or before its start time runs into the NEXT
// day: e.g. { startMinutes: 1410, endMinutes: 30 } is 11:30 PM – 12:30 AM (+1).
// `windowInstantsOn` already resolves this correctly; the helpers below give
// every UI surface the same convention for durations, labels and overlap.

/** True when a window wraps past midnight into the next day (end ≤ start). */
export function isOvernightWindow(startMinutes, endMinutes) {
  return startMinutes != null && endMinutes != null && endMinutes <= startMinutes;
}

/**
 * A window's end on a continuous minutes axis anchored at its own day's
 * midnight — overnight ends resolve past 1440 (23:30→00:30 gives 1470).
 */
export function windowEndOnAxis(startMinutes, endMinutes) {
  return isOvernightWindow(startMinutes, endMinutes) ? endMinutes + 1440 : endMinutes;
}

/** Duration in minutes, overnight-aware: 23:30→00:30 = 60. */
export function windowDurationMin(startMinutes, endMinutes) {
  return windowEndOnAxis(startMinutes, endMinutes) - startMinutes;
}

/** Friendly range label, overnight-aware: "11:30 PM – 12:30 AM (+1)". */
export function formatWindowRange(startMinutes, endMinutes) {
  const base = `${formatMinutes(startMinutes)} – ${formatMinutes(endMinutes)}`;
  return isOvernightWindow(startMinutes, endMinutes) ? `${base} (+1)` : base;
}

/**
 * Do two weekly windows overlap on the repeating 7-day cycle? Compared on the
 * same wall-clock axis (timezones intentionally ignored — matches the form's
 * historical same-day semantics). Overnight windows spill into the following
 * day, including Saturday night wrapping into Sunday of the next cycle.
 */
export function weeklyWindowsOverlap(a, b) {
  const WEEK = 7 * 1440;
  const aStart = a.dayOfWeek * 1440 + a.startMinutes;
  const aEnd = a.dayOfWeek * 1440 + windowEndOnAxis(a.startMinutes, a.endMinutes);
  const bStart = b.dayOfWeek * 1440 + b.startMinutes;
  const bEnd = b.dayOfWeek * 1440 + windowEndOnAxis(b.startMinutes, b.endMinutes);
  return [-WEEK, 0, WEEK].some((shift) =>
    rangesOverlap(aStart + shift, aEnd + shift, bStart, bEnd)
  );
}

/**
 * Resolve a recurring availability window onto a specific calendar date,
 * returning the absolute start/end instants (epoch ms). DST-safe via
 * `zonedInputToUtc`. Handles windows that cross midnight (end ≤ start) by
 * rolling the end into the next day.
 *
 * @param {string} dateKey  "YYYY-MM-DD" (the day the window falls on, in its tz)
 * @param {{startMinutes:number,endMinutes:number,timezone:string}} win
 * @returns {{startMs:number,endMs:number}|null}
 */
export function windowInstantsOn(dateKey, win) {
  if (!dateKey || !win) return null;
  const { startMinutes, endMinutes, timezone } = win;
  const startMs = zonedInputToUtc(timezone, dateKey, minutesToHHMM(startMinutes));
  let endMs = zonedInputToUtc(timezone, dateKey, minutesToHHMM(endMinutes));
  if (startMs == null || endMs == null) return null;
  if (endMs <= startMs) endMs += 24 * 60 * 60 * 1000; // crosses midnight
  return { startMs, endMs };
}

/** Absolute start/end (ms) for a one-time slot or class given start + duration. */
export function spanMs(value, durationMin = 60) {
  const d = toDate(value);
  if (!d) return null;
  const startMs = d.getTime();
  return { startMs, endMs: startMs + (Number(durationMin) || 60) * 60 * 1000 };
}

/**
 * Classify a class for calendar colouring — see `EVENT_KINDS` in
 * `features/scheduling/constants` for the chip each kind paints.
 *
 * Settled outcomes win over 'rescheduled', because once a class has actually
 * happened (or provably hasn't) that is the more useful thing to read off the
 * grid; a still-upcoming class that was moved keeps the 'rescheduled' mark.
 * Statuses this used to collapse into 'scheduled' — completed, and the two
 * no-shows — now carry their own chip: a finished session and a mentor no-show
 * looked identical on every calendar, which is the one distinction an admin
 * scanning the month actually needs.
 */
export function classEventKind(classItem) {
  if (!classItem) return 'scheduled';
  const status = classItem.status;
  if (status === 'cancelled') return 'cancelled';
  if (status === 'mentor_no_show') return 'mentor_no_show';
  if (status === 'both_no_show') return 'both_no_show';
  if (status === 'not_conducted') return 'not_conducted';
  if (status === 'student_no_show') return 'student_no_show';
  if (status === 'completed') return 'completed';
  if (classItem.rescheduled) return 'rescheduled';
  return 'scheduled';
}
